import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { API, errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  Plus, FileUp, Zap, Search, MoreVertical, Trash2, Eye, Sparkles, ArrowLeft,
  Download, CheckCircle2, Globe,
} from "lucide-react";

export default function PortfolioDetail() {
  const { pid } = useParams();
  const { t, lang, quota, refreshMe } = useApp();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [portfolio, setPortfolio] = useState(null);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // dialogs
  const [addOpen, setAddOpen] = useState(false);
  const [dName, setDName] = useState("");
  const [dCat, setDCat] = useState("");
  const [dPrice, setDPrice] = useState("");
  const [dErr, setDErr] = useState("");
  const [adding, setAdding] = useState(false);

  const [csvOpen, setCsvOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  const [genId, setGenId] = useState(null);
  const [batchRunning, setBatchRunning] = useState(false);

  const loadPortfolio = useCallback(async () => {
    try {
      const { data } = await api.get(`/portfolios/${pid}`);
      setPortfolio(data);
    } catch (err) {
      toast.error(errMsg(err, t("errGeneric")));
      navigate("/app/portfolios");
    }
  }, [pid]);

  const loadDomains = useCallback(async () => {
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get(`/portfolios/${pid}/domains`, { params });
      setDomains(data);
    } catch (err) {
      toast.error(errMsg(err, t("errGeneric")));
    } finally {
      setLoading(false);
    }
  }, [pid, search, statusFilter]);

  useEffect(() => { loadPortfolio(); }, [loadPortfolio]);
  useEffect(() => {
    const h = setTimeout(loadDomains, 250);
    return () => clearTimeout(h);
  }, [loadDomains]);

  const validClient = (v) => /^[a-z0-9.-]+$/i.test(v) && v.includes(".") && !v.includes(" ") && !/https?:\/\//i.test(v);

  const addDomain = async () => {
    setDErr("");
    const val = dName.trim();
    if (!validClient(val)) {
      setDErr(t("domainHelp"));
      return;
    }
    setAdding(true);
    try {
      await api.post(`/portfolios/${pid}/domains`, {
        domain_name: val,
        category: dCat.trim(),
        target_price: dPrice ? parseFloat(dPrice) : null,
      });
      toast.success(t("domainAdded"));
      setAddOpen(false); setDName(""); setDCat(""); setDPrice("");
      loadDomains(); loadPortfolio();
    } catch (err) {
      setDErr(errMsg(err, t("errGeneric")));
    } finally {
      setAdding(false);
    }
  };

  const importCsv = async () => {
    if (!csvFile) return;
    setImporting(true);
    setCsvResult(null);
    try {
      const form = new FormData();
      form.append("file", csvFile);
      const { data } = await api.post(`/portfolios/${pid}/domains/import`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvResult(data);
      toast.success(t("csvResult", { added: data.added_count, errors: data.error_count }));
      loadDomains(); loadPortfolio();
    } catch (err) {
      toast.error(errMsg(err, t("errGeneric")));
    } finally {
      setImporting(false);
    }
  };

  const generateOne = async (did) => {
    setGenId(did);
    try {
      const { data } = await api.post(`/domains/${did}/generate_pitch`, { language: lang });
      toast.success(t("pitchGenerated"));
      await refreshMe();
      loadDomains();
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (err?.response?.status === 402 || code === "quota_exceeded") {
        toast.error(t("quotaReached"));
      } else {
        toast.error(errMsg(err, t("errGeneric")));
      }
    } finally {
      setGenId(null);
    }
  };

  const batchGenerate = async () => {
    if (!quota?.batch) {
      toast.error(t("featureLocked"));
      navigate("/app/pricing");
      return;
    }
    setBatchRunning(true);
    try {
      const { data } = await api.post(`/portfolios/${pid}/batch_generate`, { language: lang });
      toast.success(`${t("pitchGenerated")}: ${data.generated} (${data.failed} failed)`);
      await refreshMe();
      loadDomains();
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "feature_locked") { toast.error(t("featureLocked")); navigate("/app/pricing"); }
      else if (err?.response?.status === 402) toast.error(t("quotaReached"));
      else toast.error(errMsg(err, t("errGeneric")));
    } finally {
      setBatchRunning(false);
    }
  };

  const setStatus = async (did, status) => {
    try {
      await api.put(`/domains/${did}`, { status });
      loadDomains();
    } catch (err) { toast.error(errMsg(err, t("errGeneric"))); }
  };

  const removeDomain = async (did) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/domains/${did}`);
      setDomains((p) => p.filter((d) => d.id !== did));
      loadPortfolio();
    } catch (err) { toast.error(errMsg(err, t("errGeneric"))); }
  };

  const exportCsv = async () => {
    if (!quota?.csv_export) { toast.error(t("featureLocked")); navigate("/app/pricing"); return; }
    try {
      const token = localStorage.getItem("dp_token");
      const res = await fetch(`${API}/portfolios/${pid}/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${portfolio?.name || "portfolio"}_pitches.csv`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(errMsg(err, t("errGeneric"))); }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb + header */}
      <div>
        <Link to="/app/portfolios" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t("navPortfolios")}
        </Link>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight">{portfolio?.name || <Skeleton className="h-7 w-40" />}</h1>
            {portfolio?.description && <p className="text-sm text-muted-foreground">{portfolio.description}</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Add domain */}
            <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); setDErr(""); }}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="portfolio-add-domain-button"><Plus className="h-4 w-4" /> {t("addDomain")}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("addDomain")}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>{t("domainNameLabel")}</Label>
                    <Input value={dName} onChange={(e) => setDName(e.target.value)} placeholder={t("domainNamePlaceholder")} data-testid="add-domain-name-input" className="font-mono" />
                    <p className="text-xs text-muted-foreground">{t("domainHelp")}</p>
                    {dErr && <p className="text-xs text-destructive" data-testid="add-domain-error">{dErr}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>{t("categoryLabel")}</Label>
                      <Input value={dCat} onChange={(e) => setDCat(e.target.value)} placeholder="FinTech" data-testid="add-domain-category-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t("priceLabel")}</Label>
                      <Input type="number" min="0" value={dPrice} onChange={(e) => setDPrice(e.target.value)} placeholder="10000" data-testid="add-domain-price-input" />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setAddOpen(false)}>{t("cancel")}</Button>
                  <Button onClick={addDomain} disabled={adding || !dName.trim()} data-testid="add-domain-submit">{adding ? t("loading") : t("addDomain")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* CSV import */}
            <Dialog open={csvOpen} onOpenChange={(o) => { setCsvOpen(o); setCsvResult(null); setCsvFile(null); }}>
              <DialogTrigger asChild>
                <Button variant="secondary" className="gap-2" data-testid="portfolio-import-csv-button"><FileUp className="h-4 w-4" /> {t("importCsv")}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("csvDialogTitle")}</DialogTitle>
                  <DialogDescription>{t("csvDialogDesc")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <Input ref={fileRef} type="file" accept=".csv,text/csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} data-testid="csv-file-input" />
                  {csvResult && (
                    <div className="rounded-md border bg-secondary/40 p-3 text-sm" data-testid="csv-import-result">
                      <div className="flex items-center gap-2 font-medium"><CheckCircle2 className="h-4 w-4 text-primary" /> {t("csvResult", { added: csvResult.added_count, errors: csvResult.error_count })}</div>
                      {csvResult.errors?.length > 0 && (
                        <ul className="mt-2 max-h-32 space-y-1 overflow-auto text-xs text-muted-foreground">
                          {csvResult.errors.slice(0, 20).map((e, i) => (
                            <li key={i}>Row {e.row}: <span className="font-mono">{e.value || "—"}</span> — {e.error}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setCsvOpen(false)}>{t("close")}</Button>
                  <Button onClick={importCsv} disabled={importing || !csvFile} data-testid="csv-import-submit">{importing ? t("loading") : t("csvImport")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button variant="secondary" className="gap-2" onClick={batchGenerate} disabled={batchRunning} data-testid="portfolio-batch-generate-button">
              <Zap className="h-4 w-4" /> {batchRunning ? t("generating") : t("batchGenerate")}
            </Button>
            {quota?.csv_export && (
              <Button variant="outline" className="gap-2" onClick={exportCsv} data-testid="portfolio-export-csv-button">
                <Download className="h-4 w-4" /> {t("exportCsv")}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("searchDomains")} className="pl-9" data-testid="domains-table-search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="domains-table-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatuses")}</SelectItem>
              <SelectItem value="new">{t("statusNew")}</SelectItem>
              <SelectItem value="pitched">{t("statusPitched")}</SelectItem>
              <SelectItem value="sold">{t("statusSold")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      {loading ? (
        <Skeleton className="h-64" />
      ) : domains.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary"><Globe className="h-6 w-6" /></span>
          <h3 className="font-heading text-lg font-semibold">{t("noDomains")}</h3>
          <p className="max-w-sm text-sm text-muted-foreground">{t("noDomainsDesc")}</p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <Table data-testid="domains-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wide">{t("colDomain")}</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide">{t("colCategory")}</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide">{t("colPrice")}</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide">{t("colStatus")}</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-wide">{t("colActions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {domains.map((d) => (
                  <TableRow key={d.id} className="group" data-testid="domain-row">
                    <TableCell>
                      <button className="font-mono text-sm font-medium hover:text-primary hover:underline" onClick={() => navigate(`/app/domains/${d.id}`)} data-testid="domain-name-link">
                        {d.domain_name}
                      </button>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{d.category || "—"}</TableCell>
                    <TableCell className="font-mono text-sm">{d.target_price ? `€${Number(d.target_price).toLocaleString()}` : "—"}</TableCell>
                    <TableCell><StatusBadge status={d.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!d.has_pitch ? (
                          <Button size="sm" className="gap-1.5" onClick={() => generateOne(d.id)} disabled={genId === d.id} data-testid="domain-generate-button">
                            <Sparkles className="h-3.5 w-3.5" /> {genId === d.id ? t("generating") : t("generate")}
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => navigate(`/app/domains/${d.id}`)} data-testid="domain-view-button">
                            <Eye className="h-3.5 w-3.5" /> {t("view")}
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-8 w-8" data-testid="domain-menu"><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/app/domains/${d.id}`)}><Eye className="mr-2 h-4 w-4" /> {t("view")}</DropdownMenuItem>
                            {d.status !== "sold" ? (
                              <DropdownMenuItem onClick={() => setStatus(d.id, "sold")}><CheckCircle2 className="mr-2 h-4 w-4" /> {t("markSold")}</DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => setStatus(d.id, "new")}>{t("markNew")}</DropdownMenuItem>
                            )}
                            <DropdownMenuItem className="text-destructive" onClick={() => removeDomain(d.id)}><Trash2 className="mr-2 h-4 w-4" /> {t("delete")}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
